import { useRef, useEffect, useState } from "react";

interface UseSecondScrollbarReturn {
  gridWrapperRef: React.RefObject<HTMLDivElement>;
  topScrollRef: React.RefObject<HTMLDivElement>;
  spacerRef: React.RefObject<HTMLDivElement>;
  showScrollHint: boolean;
  dismissHint: () => void;
}

export const useDoubleScrollbar = (
  gridContentSelector: string = ".k-grid-content"
): UseSecondScrollbarReturn => {
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const [hasScrolledHorizontally, setHasScrolledHorizontally] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const dismissHint = () => {
    setShowScrollHint(false);
    setHasScrolledHorizontally(true);
  };

  useEffect(() => {
    const gridWrapper = gridWrapperRef.current;
    const topScroll = topScrollRef.current;
    const spacer = spacerRef.current;

    if (!gridWrapper || !topScroll || !spacer) return;

    const updateSpacerWidth = () => {
      const gridContent = gridWrapper.querySelector(gridContentSelector);
      if (gridContent) {
        spacer.style.width = `${gridContent.scrollWidth}px`;
      }
    };

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const isHorizontalScroll = target.scrollLeft > 0;
      const isVerticalScroll = target.scrollTop > 0;

      if (isHorizontalScroll && !hasScrolledHorizontally) {
        setHasScrolledHorizontally(true);
        setShowScrollHint(false);
      } else if (isVerticalScroll && !hasScrolledHorizontally) {
        setShowScrollHint(true);
      }

      if (target.matches(gridContentSelector)) {
        topScroll.scrollLeft = target.scrollLeft;
      } else if (target === topScroll) {
        const gridContent = gridWrapper.querySelector(gridContentSelector);
        if (gridContent) {
          (gridContent as HTMLElement).scrollLeft = target.scrollLeft;
        }
      }
    };

    // Initial setup
    updateSpacerWidth();

    // Set up observers
    const resizeObserver = new ResizeObserver(updateSpacerWidth);
    resizeObserver.observe(gridWrapper);

    // Observe the grid content element for changes
    const gridContent = gridWrapper.querySelector(gridContentSelector);
    if (gridContent) {
      resizeObserver.observe(gridContent);
    }

    // Set up a mutation observer to watch for the grid content element
    const mutationObserver = new MutationObserver((mutations) => {
      const gridContent = gridWrapper.querySelector(gridContentSelector);
      if (gridContent) {
        updateSpacerWidth();
        resizeObserver.observe(gridContent);
      }
    });

    mutationObserver.observe(gridWrapper, {
      childList: true,
      subtree: true,
    });

    gridWrapper.addEventListener("scroll", handleScroll, true);
    topScroll.addEventListener("scroll", handleScroll);

    return () => {
      gridWrapper.removeEventListener("scroll", handleScroll, true);
      topScroll.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [gridContentSelector, hasScrolledHorizontally]);

  return {
    gridWrapperRef,
    topScrollRef,
    spacerRef,
    showScrollHint,
    dismissHint,
  };
};
